// API URL из переменной окружения или значение по умолчанию
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Получение токена из localStorage
const getToken = () => localStorage.getItem('token');

// Заголовки с авторизацией
const authHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Проверка ответа на авторизацию
const checkAuth = (response) => {
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
    throw new Error('Сессия истекла');
  }
  return response;
};

// API авторизации
export const authApi = {
  // Вход
  async login(username, password) {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка входа');
    }
    return response.json();
  },

  // Получить текущего пользователя
  async getMe() {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: authHeaders(),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Ошибка получения пользователя');
    return response.json();
  },

  // Выход
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

// API пользователей (только для админа)
export const usersApi = {
  // Получить всех пользователей
  async getAll() {
    const response = await fetch(`${API_URL}/admin/users`, {
      headers: authHeaders(),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Ошибка загрузки пользователей');
    return response.json();
  },

  // Создать пользователя
  async create(userData) {
    const response = await fetch(`${API_URL}/admin/users`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(userData),
    });
    checkAuth(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка создания пользователя');
    }
    return response.json();
  },

  // Обновить пользователя
  async update(id, userData) {
    const response = await fetch(`${API_URL}/admin/users/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(userData),
    });
    checkAuth(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка обновления');
    }
    return response.json();
  },

  // Удалить пользователя
  async delete(id) {
    const response = await fetch(`${API_URL}/admin/users/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    checkAuth(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка удаления');
    }
    return response.json();
  },
};

export const rangesApi = {
  // Получить список всех диапазонов
  async getAll() {
    const response = await fetch(`${API_URL}/ranges`, {
      headers: authHeaders(),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Ошибка загрузки диапазонов');
    return response.json();
  },

  // Получить диапазон по ID
  async getById(id) {
    const response = await fetch(`${API_URL}/ranges/${id}`, {
      headers: authHeaders(),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Диапазон не найден');
    return response.json();
  },

  // Создать новый диапазон
  async create(rangeData) {
    const response = await fetch(`${API_URL}/ranges`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(rangeData),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Ошибка создания диапазона');
    return response.json();
  },

  // Обновить диапазон
  async update(id, rangeData) {
    const response = await fetch(`${API_URL}/ranges/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(rangeData),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Ошибка обновления диапазона');
    return response.json();
  },

  // Удалить диапазон
  async delete(id) {
    const response = await fetch(`${API_URL}/ranges/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    checkAuth(response);
    if (!response.ok) throw new Error('Ошибка удаления диапазона');
    return response.json();
  },

  // Получить позиции
  async getPositions() {
    const response = await fetch(`${API_URL}/positions`);
    if (!response.ok) throw new Error('Ошибка загрузки позиций');
    return response.json();
  },
};

// Ранги карт
export const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];

// Позиции за столом
export const POSITIONS = ['UTG', 'UTG1', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];

// Позиции противника (включая специальные)
export const VS_POSITIONS = ['RFI', 'MUX', 'UTG', 'UTG1', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];

// Генерация названия руки по координатам
export const getHandName = (row, col) => {
  const r1 = RANKS[row];
  const r2 = RANKS[col];
  
  if (row === col) {
    return `${r1}${r2}`; // Пара
  } else if (row < col) {
    return `${r1}${r2}s`; // Suited
  } else {
    return `${r2}${r1}o`; // Offsuit
  }
};

// Генерация пустой сетки
export const generateEmptyGrid = () => {
  const grid = {};
  for (let row = 0; row < 13; row++) {
    for (let col = 0; col < 13; col++) {
      const hand = getHandName(row, col);
      grid[hand] = { allin: 0, '3bet': 0, raise: 0, call: 0, exploit: 0, superexploit: 0, fold: 100 };
    }
  }
  return grid;
};

// Фиксированные цвета действий (не зависят от БД)
export const ACTION_COLORS = {
  allin: '#691616',
  '3bet': '#61489B',
  raise: '#992E2E',
  call: '#79A65B',
  exploit: '#D7E041',
  superexploit: '#E7E0E5',
  fold: '#33343A',
};

// Функция определения контрастного цвета текста
export const getContrastColor = (hexColor) => {
  if (!hexColor) return '#ffffff';
  
  // Убираем # если есть
  const hex = hexColor.replace('#', '');
  
  // Конвертируем в RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Рассчитываем яркость по формуле (perceived luminance)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Если яркость > 0.5 - тёмный текст, иначе белый
  return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
};

// Действия по умолчанию
export const DEFAULT_ACTIONS = [
  { id: 'allin', name: 'Allin 200', color: ACTION_COLORS.allin, percentage: 0 },
  { id: '3bet', name: '3bet', color: ACTION_COLORS['3bet'], percentage: 0 },
  { id: 'raise', name: 'Raise 2.5', color: ACTION_COLORS.raise, percentage: 0 },
  { id: 'call', name: 'Call', color: ACTION_COLORS.call, percentage: 0 },
  { id: 'exploit', name: 'Exploit', color: ACTION_COLORS.exploit, percentage: 0 },
  { id: 'superexploit', name: 'Super Exploit', color: ACTION_COLORS.superexploit, percentage: 0 },
  { id: 'fold', name: 'Fold', color: ACTION_COLORS.fold, percentage: 100 },
];
